<template>
  <div>
    <header class="mb-2">
      <div class="flex items-baseline justify-between gap-3">
        <h3 class="text-sm font-bold text-white">Sportsbook lines</h3>
        <!-- Every number in this panel is written from the home team's side,
             the way the tug bar above it and the database both are, so the
             strip reads as a zoom of that bar. The edge side still drives the
             order and the colour — it is only the sign that is fixed. -->
        <p class="text-[11px] text-slate-400">for <strong class="font-semibold text-slate-200">{{ homeAbbr }}</strong> at home</p>
      </div>
      <MarketStrip
        :frozen="frozenLine" :market="marketLine" :rows="rows"
        :home-abbr="homeAbbr" :away-abbr="awayAbbr" :edge-direction="edgeDirection"
        :highlight="focusedBook" :sharp-mean="split.sharp" :retail-mean="split.retail"
        class="mt-1" @select="focusedBook = $event"
      />
      <!-- The three numbers the panel is actually a comparison between, in the
           order they are read: what we hold, then the two cohorts whose gap
           says where the rest of the market is going. Each swatch is the mark
           its own value carries on the strip above — white rule, sky, violet —
           so this row doubles as the strip's legend rather than being a second
           thing to learn.
           The frozen number lives here rather than under the "Posts" header,
           where it cost the whole table a second header line to say something
           that belongs next to the numbers it is compared against. -->
      <div v-if="frozenLine != null || split.sharp != null" class="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] tabular-nums">
        <span v-if="frozenLine != null" class="flex items-center gap-1.5">
          <span class="cohort-swatch cohort-frozen"></span>
          <span class="text-slate-400">Frozen</span>
          <strong class="font-semibold text-slate-200">{{ formatSigned(frozenLine) }}</strong>
        </span>
        <span v-if="split.sharp != null" class="flex items-center gap-1.5">
          <span class="cohort-swatch"></span>
          <span class="text-slate-400">Sharp</span>
          <strong class="font-semibold text-slate-200">{{ formatSigned(split.sharp) }}</strong>
          <span class="text-slate-500">({{ split.sharpCount }})</span>
        </span>
        <span v-if="split.retail != null" class="flex items-center gap-1.5">
          <span class="cohort-swatch cohort-retail"></span>
          <span class="text-slate-400">Retail</span>
          <strong class="font-semibold text-slate-200">{{ formatSigned(split.retail) }}</strong>
          <span class="text-slate-500">({{ split.retailCount }})</span>
        </span>
      </div>
      <!-- Where the market is, and where it is still going. The posted-movement
           count that used to close this sentence has moved to the foot of the
           list and been reworded — it is a fact about which books you can still
           bet, so it belongs beside the books rather than up here, and "7 have
           moved past ours" was the arithmetic where "only FanDuel still posts
           it" is the consequence. The methodology gloss is gone too: the
           columns are labelled "Posts" and "Fair", which says it in two words
           instead of seventeen. -->
      <!-- The `{{ " " }}` before the note is load-bearing. A literal space and
           an `&#32;` are both decoded and then stripped by the template
           compiler, which trims leading whitespace inside an element, so the
           sentence renders as "our side.Retail trails". An interpolation is
           data rather than markup and survives. -->
      <p v-if="comparedCount" class="mt-1 text-[11px] leading-relaxed text-slate-400">
        <strong class="text-slate-200">{{ tally.for }} of {{ tally.compared }} books</strong> favour {{ edgeSideAbbr }}<span v-if="tally.against">, <strong class="text-slate-200">{{ tally.against }}</strong> the other</span><span v-if="agreementLabel">, and they agree within <strong class="text-slate-200">{{ agreementLabel }}</strong></span>.<span v-if="splitNote">{{ " " }}{{ splitNote }}</span>
      </p>
      <p v-else-if="rows.length" class="mt-1 text-[11px] text-slate-400">Alphabetical until a frozen line arrives.</p>
    </header>
    <p v-if="!rows.length" class="py-2 text-xs text-slate-400">No sportsbook lines yet.</p>
    <template v-else>
      <div class="book-columns border-b border-white/10 pb-2 text-right text-[11px] text-slate-400">
        <span class="text-left">Sportsbook</span>
        <span class="numbers">
          <span class="cell-posted">Posts</span>
          <!-- Named here rather than on every row: "-106 / -114" is ambiguous on
               its own, and the phone is where the panel is actually used. -->
          <span class="cell-odds">{{ homeAbbr }} / {{ awayAbbr }}</span>
          <span class="cell-fair">Fair</span>
        </span>
      </div>
      <ul>
        <li v-for="row in rows" :key="row.book.bookmaker" class="book-columns min-h-[44px] items-center rounded border-b border-white/[0.07] py-1.5 transition-colors"
          :class="focusedBook === row.book.bookmaker ? 'bg-white/[0.06]' : ''"
          @mouseenter="focusedBook = row.book.bookmaker"
          @mouseleave="focusedBook = null">
          <div class="flex min-w-0 items-center gap-2">
            <img v-if="row.book.logo" :src="row.book.logo" alt="" class="h-5 w-5 shrink-0 rounded object-contain" :class="logoNeedsLightBackground(row.book.bookmaker) ? 'bg-slate-100 p-0.5' : ''" />
            <span v-else class="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-white/[0.06] text-[9px] font-bold text-slate-400" aria-hidden="true">{{ bookmakerInitials(row.book.bookmaker) }}</span>
            <div class="min-w-0">
              <span class="flex items-center gap-1.5">
                <span class="truncate text-xs font-semibold text-slate-200">{{ bookmakerName(row.book.bookmaker) }}</span>
                <!-- Prices to a thin margin and moves on money rather than on
                     the other books, so its number is an estimate of the truth
                     rather than partly a record of where the public is. Same
                     books that count double in the consensus. -->
                <span v-if="sharp(row)" class="sharp-tag shrink-0">sharp</span>
              </span>
              <span class="block text-[10px] leading-tight text-slate-500">{{ quoteAge(row.book) }}</span>
            </div>
          </div>
          <!-- The de-vigged number leads. Nobody bets at these books — the pool
               is the bet, and the books are evidence of where the market really
               is — so the number that sets the colour and the rank is the one
               worth showing big, with what the book posts and the price pair
               that moves it alongside. -->
          <div class="numbers tabular-nums">
            <span class="cell-posted whitespace-nowrap text-[11px] text-slate-400">{{ postedLine(row) }}</span>
            <span class="cell-odds whitespace-nowrap text-[10px] text-slate-500 sm:text-[11px]">{{ priceLabel(row) }}</span>
            <span class="cell-fair">
              <span class="chip text-sm font-bold" :class="{ 'chip-none': row.points == null }" :style="{ '--edge': rowColor(row) }" :title="differenceLabel(row)"><span class="chip-team">{{ homeAbbr }}</span>{{ formatSigned(displayLine(row)) }}</span>
            </span>
          </div>
        </li>
      </ul>
      <!-- The takeaway, under the evidence it is drawn from and beside the row
           it names. Read after the list rather than before it: every other line
           in this panel is a summary you take on trust, and this is the one you
           can check by running an eye up the Posts column. -->
      <p v-if="availabilityNote" class="mt-2 text-[11px] leading-relaxed text-slate-400">
        {{ availabilityNote }}
      </p>
    </template>
  </div>
</template>

<script>
import { bookTally, computeEdge, fairSpread, formatLine, formatSigned, frozenAvailability, isSharpBook, postedMovementTally, sharpSplit, SHARP_SPLIT_EPSILON } from "../utils/edge.js";
import { agreementLabel, movedNote } from "../utils/verdictLine.js";
import { bookQuoteAge } from "../utils/marketAge.js";
import { rankBooks } from "../utils/bookRanking.js";
import { edgeTone } from "../utils/edgeTone.js";
import MarketStrip from "./MarketStrip.vue";
import { BOOKMAKER_NAMES } from "../utils/bookmakerNames.js";


export default {
  name: "BookmakerTable",
  components: { MarketStrip },
  props: {
    game: { type: Object, required: true },
  },
  data() {
    return { focusedBook: null };
  },
  computed: {
    edge() {
      return computeEdge(this.game);
    },
    // The side the edge is on: it still decides what counts as a good number,
    // it just no longer decides which way the numbers are written.
    comparisonSide() { return this.edge.side || "home"; },
    edgeDirection() { return this.comparisonSide === "away" ? -1 : 1; },
    homeAbbr() { return this.game.homeTeam.abbr; },
    awayAbbr() { return this.game.awayTeam.abbr; },
    rows() { return rankBooks(this.game, this.comparisonSide, "home"); },
    frozenLine() { return this.edge.frozen == null ? null : this.edge.frozen; },
    marketLine() { return this.edge.market == null ? null : this.edge.market; },
    // Counted on the de-vigged number, the same one the dots are plotted and
    // coloured by — see `bookTally`. Counting posted lines here put a "4/11"
    // above a strip showing eight dots for and three against, in a sentence
    // that then said the lines were de-vigged.
    tally() { return bookTally(this.game, this.edge); },
    // Which books will still give us the frozen number — see
    // `frozenAvailability`. Reads the literal posted number.
    availability() { return frozenAvailability(this.game, this.edge); },
    // Posted numbers that changed our OUTRIGHT win set — a different fact from
    // the de-vigged `tally` above, with its own words ("moved off it").
    movement() { return postedMovementTally(this.game, this.edge); },
    comparedCount() { return this.tally.compared; },
    edgeSideAbbr() { return this.comparisonSide === "away" ? this.awayAbbr : this.homeAbbr; },
    // σ of the books' de-vigged numbers, moved here off the card's line.
    agreementLabel() { return agreementLabel(this.game); },
    // Named in the panel's own home-perspective terms, the same string the
    // strip's captions print for the same number — the panel says "for KC at
    // home" once at the top, and flipping to the edge's side down here would
    // make the reader reconcile two conventions in one card.
    frozenLabel() { return formatLine(this.frozenLine, this.homeAbbr, this.awayAbbr); },
    availabilityNote() {
      const sentence = this.availabilitySentence;
      if (!sentence) return "";
      const moved = movedNote(this.movement);
      return moved ? `${sentence.slice(0, -1)} ${moved}.` : sentence;
    },
    availabilitySentence() {
      const { count, compared, only, closest } = this.availability;
      if (!compared || this.frozenLine == null) return "";

      if (only) {
        // Worth saying only when the book shows our number but no longer
        // believes it — that disagreement is the reason `Fair` is a column,
        // and this is the one row where it decides something.
        const fair = fairSpread(only);
        const doubt =
          fair == null || Math.abs(fair - this.frozenLine) < 0.05
            ? ""
            : `, though its price says ${formatSigned(fair)}`;
        return `Only ${this.bookmakerName(only.bookmaker)} still posts ${this.frozenLabel}${doubt}.`;
      }
      if (count) {
        return `${count} of ${compared} books still post ${this.frozenLabel} or better.`;
      }
      return closest
        ? `No book still posts ${this.frozenLabel} — the closest is ${this.bookmakerName(closest.bookmaker)} at ${formatSigned(closest.homeSpread)}.`
        : `No book still posts ${this.frozenLabel}.`;
    },
    // Sharp books against retail books, on the same de-vigged numbers the dots
    // are plotted at — see `sharpSplit`.
    split() { return sharpSplit(this.game, this.edge); },
    // Only written out when both cohorts are big enough to mean anything and
    // the gap is wider than rounding. The values themselves are always shown;
    // it is the *reading* of them that has to earn its place, because "retail
    // is 0.04 behind" is a sentence about nothing.
    splitNote() {
      const { usable, lead } = this.split;
      if (!usable || lead == null || Math.abs(lead) < SHARP_SPLIT_EPSILON) return "";
      const gap = Math.abs(lead).toFixed(1);
      return lead > 0
        ? `Retail trails the sharps by ${gap} — the move our way may not be finished.`
        : `Retail has run ${gap} past the sharps — that move is public money, not sharp money.`;
    },
  },
  methods: {
    formatSigned,
    sharp(row) {
      return isSharpBook(row.book.bookmaker);
    },
    // The shared diverging scale — see `edgeTone`; the strip's dots use it too.
    rowColor(row) {
      return edgeTone(row);
    },
    // The chip's number: the de-vigged one wherever there is a price to de-vig,
    // falling back to the posted number, which is all a pricless book has.
    displayLine(row) {
      return row.fairLine == null ? row.line : row.fairLine;
    },
    // Bare number: the column header says "Posts", so repeating the word on ten
    // rows only costs the width that keeps the row on one line.
    postedLine(row) {
      return row.line == null ? "—" : formatSigned(row.line);
    },
    // Both sides, the compared team's first — the pair is what the fair number
    // is derived from, and one price alone makes two different rows look
    // identical. Which team is which is named in the column header.
    priceLabel(row) {
      if (row.price == null) return "";
      if (row.oppositePrice == null) return this.formatPrice(row.price);
      return `${this.formatPrice(row.price)} / ${this.formatPrice(row.oppositePrice)}`;
    },
    differenceLabel(row) {
      if (row.points == null) return "No line posted";
      const posted = row.points === 0
        ? "Posts the pool line"
        : row.points > 0
          ? `Pool line is ${formatSigned(row.points)} better than posted`
          : `Posts ${formatSigned(-row.points)} better than the pool line`;
      if (row.fairLine == null || Math.abs(row.fairLine - row.line) < 0.05) return posted;
      return `${posted} · worth ${formatSigned(row.fairLine)} once the vig is out`;
    },
    bookmakerName(bookmaker) {
      const key = String(bookmaker || "").toLowerCase();
      return (
        BOOKMAKER_NAMES[key] ||
        String(bookmaker || "Sportsbook")
          .replaceAll("_", " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase())
      );
    },
    bookmakerInitials(bookmaker) {
      return this.bookmakerName(bookmaker)
        .split(/\s+/)
        .map((word) => word[0])
        .join("")
        .slice(0, 3);
    },
    logoNeedsLightBackground(bookmaker) {
      return ["betonlineag", "draftkings", "novig", "pinnacle", "polymarket", "williamhill_us"].includes(
        String(bookmaker || "").toLowerCase()
      );
    },
    formatPrice(price) {
      return price == null ? "—" : formatSigned(price, 0);
    },
    // From now before the deadline, from the cutoff after it -- see
    // `bookQuoteAge`.
    quoteAge(book) {
      return bookQuoteAge(book, this.game);
    },
  },
};
</script>

<style scoped>
/* The team rides at the chip's shoulder: present enough that the number is
   never read as the other side's line, quiet enough that eleven rows of the
   same three letters do not compete with the numbers they qualify. */
.chip-team {
  margin-right: 0.28em;
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.02em;
  opacity: 0.7;
}

/* One line at every width. The numbers stacked under one another on a phone
   before, which made each row three lines tall for three short values that fit
   side by side once "posts" came out of them. */
.book-columns { display: grid; grid-template-columns: minmax(0, 1fr) 2.4rem 4.3rem 4.9rem; column-gap: .3rem; align-items: center; }
.numbers { display: contents; }
.cell-posted, .cell-odds, .cell-fair { text-align: right; }
@media (min-width: 640px) {
  .book-columns { grid-template-columns: minmax(0, 1fr) 5rem 6.5rem 6.75rem; column-gap: .75rem; }
}

/* Reads the marks on the strip rather than decorating the words: same
   colours, same order (SHARP_COLOR / RETAIL_COLOR in MarketStrip). */
.cohort-swatch {
  --cohort: #7dd3fc;
  display: inline-block;
  width: 2px;
  height: 0.65rem;
  border-radius: 999px;
  background: var(--cohort);
}
/* The same white the strip's frozen rule is drawn in. */
.cohort-frozen { --cohort: #ffffff; }
.cohort-retail { --cohort: #c4b5fd; }

/* The sharp-book tag. */
.sharp-tag {
  border-radius: 999px;
  background: rgb(56 189 248 / 0.14);
  padding: 0.05rem 0.35rem;
  font-size: 0.55rem;
  font-weight: 800;
  color: #7dd3fc;
  text-transform: uppercase;
}

/* Diverging scale, validated against this panel's surface (#16304c) for
   monotone lightness within each pole and for pole separation under simulated
   protan/deutan vision — green vs amber is the pair that most often collapses,
   so it was measured rather than eyeballed (ΔE 11.6 deutan, 25.0 normal). */
.chip {
  display: inline-block;
  min-width: 4.4rem;
  border-radius: .375rem;
  padding: .125rem .375rem;
  color: var(--edge);
  background: color-mix(in oklab, var(--edge) 14%, transparent);
  box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--edge) 34%, transparent);
}
/* No posted line is an absence, not a weak edge — no chip at all. */
.chip-none { background: none; box-shadow: none; }
</style>
